import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alertService } from '../../services/alertService';
import {
  getSeverityIcon,
  getSeverityColor,
  formatRelativeTime,
  getCategoryDisplayName
} from '../../utils/alertHelpers';
import styles from './AlertCenter.module.css';

/**
 * AlertCenter Component
 *
 * Displays and manages user alerts with filtering
 * Shows critical, warning, and info alerts
 */

export default function AlertCenter({ uid, organizationId = null }) {
  const { t } = useTranslation('statistics');
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [loading, setLoading] = useState(true);

  // Subscribe to alerts
  useEffect(() => {
    if (!uid) return;

    // Se suscribe SIN filtro de severidad y se filtra en memoria (abajo).
    //
    // Dos razones. La primera: para poder auto-ocultarse hace falta saber si hay
    // CERO alertas, y una suscripción filtrada por severidad no lo puede
    // responder — "no hay críticas" no es "no hay ninguna". La segunda: cambiar
    // de pestaña dejaba de re-suscribir a Firestore, así que ahora es instantáneo
    // y no depende del índice compuesto (dismissed, severity, createdAt).
    const unsubscribe = alertService.subscribeToAlerts(
      uid,
      { severity: 'all', limit: 50, organizationId },
      (newAlerts) => {
        setAlerts(newAlerts);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [uid, organizationId]);

  // Filtro de severidad, en memoria sobre el conjunto completo.
  useEffect(() => {
    setFilteredAlerts(
      selectedSeverity === 'all'
        ? alerts
        : alerts.filter((a) => a.severity === selectedSeverity)
    );
  }, [alerts, selectedSeverity]);

  // Handle severity filter change
  const handleFilterChange = (severity) => {
    setSelectedSeverity(severity);
  };

  // Handle dismiss alert
  const handleDismiss = async (alertId) => {
    try {
      await alertService.dismissAlert(uid, alertId, organizationId);
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  // Handle mark as read
  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService.markAsRead(uid, alertId, organizationId);
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  // Handle alert action click
  //
  // `actionUrl` de las alertas del bot es `/chat/<chatId>`: el aviso no sirve si
  // el dueño tiene que abrir 17 conversaciones para encontrar cuál se cayó.
  const handleAction = (alert) => {
    handleMarkAsRead(alert.id);
    if (alert.actionUrl) {
      navigate(alert.actionUrl);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>{t('alerts.loading')}</div>
      </div>
    );
  }

  // Sin alertas, el componente no ocupa lugar.
  //
  // El gate vive ACÁ y no en el Dashboard a propósito. Antes dependía de
  // `alerts_summary.totalCount`, que lo escribe un job periódico: entre que se
  // creaba una alerta y corría el agregador, el dueño no veía algo que YA
  // existía. Peor todavía, `alerts_summary` no tenía regla de lectura, así que
  // el resumen llegaba como null y el panel no aparecía nunca.
  //
  // Esto se decide con la MISMA lista que se va a renderizar. Si hay algo para
  // mostrar, se muestra; y un panel "no hay alertas" permanente enseña a
  // ignorar el lugar donde después aparece la que importa.
  if (alerts.length === 0) return null;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>{t('alerts.title')}</h2>
        <p className={styles.subtitle}>
          {t('alerts.count', { count: filteredAlerts.length })}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className={styles.filters}>
        <button
          className={`${styles.filterBtn} ${selectedSeverity === 'all' ? styles.active : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          {t('alerts.all')}
        </button>
        <button
          className={`${styles.filterBtn} ${styles.critical} ${selectedSeverity === 'critical' ? styles.active : ''}`}
          onClick={() => handleFilterChange('critical')}
        >
          {t('alerts.critical')}
        </button>
        <button
          className={`${styles.filterBtn} ${styles.warning} ${selectedSeverity === 'warning' ? styles.active : ''}`}
          onClick={() => handleFilterChange('warning')}
        >
          {t('alerts.warnings')}
        </button>
        <button
          className={`${styles.filterBtn} ${styles.info} ${selectedSeverity === 'info' ? styles.active : ''}`}
          onClick={() => handleFilterChange('info')}
        >
          {t('alerts.info')}
        </button>
      </div>

      {/* Alerts List */}
      <div className={styles.alertsList}>
        {filteredAlerts.length === 0 ? (
          <div className={styles.empty}>
            <p>{t('alerts.empty')}</p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div
              key={alert.id}
              className={`${styles.alertCard} ${styles[alert.severity]} ${alert.read ? styles.read : ''}`}
            >
              {/* Alert Icon */}
              <div className={styles.iconContainer}>
                <span className={styles.icon}>{getSeverityIcon(alert.severity)}</span>
              </div>

              {/* Alert Content */}
              <div className={styles.content}>
                <div className={styles.contentHeader}>
                  <h3 className={styles.alertTitle}>{alert.title}</h3>
                  <span className={styles.category}>
                    {getCategoryDisplayName(alert.category)}
                  </span>
                </div>

                <p className={styles.message}>{alert.message}</p>

                <div className={styles.meta}>
                  <span className={styles.time}>
                    {formatRelativeTime(alert.createdAt)}
                  </span>
                  {alert.ocurrencias > 1 && (
                    <span className={styles.metadataBadge}>
                      {t('alerts.occurrences', { count: alert.ocurrencias })}
                    </span>
                  )}
                </div>

                {/* Actions */}
                {alert.actionRequired && (
                  <div className={styles.actions}>
                    <button
                      className={styles.actionBtn}
                      onClick={() => handleAction(alert)}
                    >
                      {alert.actionLabel || t('alerts.viewMore')}
                    </button>
                  </div>
                )}
              </div>

              {/* Dismiss Button */}
              <button
                className={styles.dismissBtn}
                onClick={() => handleDismiss(alert.id)}
                title={t('alerts.dismiss')}
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
